import {
  displayValue,
  leafUnit,
  type SettingsGroupView,
  type SettingsOverride,
} from '@pypestream/ui-patterns';

export interface OverriddenGroup {
  group: string;
  view: SettingsGroupView;
  overrides: SettingsOverride[];
}

export interface OverriddenSetting {
  key: string;
  label: string;
  value: string;
  orgDefault: string;
  /** The level pins the group without changing any value in it. */
  same?: boolean;
}

export interface OverriddenBy {
  key: string;
  scopeType: SettingsOverride['scopeType'];
  /** The org's or project's name; for an application, its project's. */
  name: string | null;
  settings: OverriddenSetting[];
}

export interface OverriddenInText {
  labelFor: (leaf: string) => string;
  groupLabel: (group: string) => string;
  notSet: string;
  sameAsDefault: string;
}

const withUnit = (leaf: string, value: unknown) => {
  const unit = leafUnit(leaf);
  return unit && typeof value === 'number'
    ? `${displayValue(value)} ${unit}`
    : displayValue(value);
};

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * One row per level below the org that overrides any of `groups`, listing the
 * settings where it differs from the org's value. A layer stores its whole
 * group, so one that differs nowhere still pins the group and gets one row
 * saying so.
 */
export function overriddenInRows(
  groups: OverriddenGroup[],
  text: OverriddenInText
): OverriddenBy[] {
  const rows = new Map<string, OverriddenBy>();

  groups.forEach(({ group, view, overrides }) => {
    const defaults = view.values ?? view.inherited.values ?? {};

    overrides.forEach((override, index) => {
      // The API names an application by its project only, so two applications
      // of one project can't be told apart across groups: never merge them.
      const key =
        override.scopeType === 'application'
          ? `application:${group}:${index}`
          : `${override.scopeType}:${override.name ?? ''}`;
      const row = rows.get(key) ?? {
        key,
        scopeType: override.scopeType,
        name: override.name,
        settings: [],
      };
      rows.set(key, row);

      const leaves = [
        ...view.leaves,
        ...Object.keys(override.values).filter((l) => !view.leaves.includes(l)),
      ].filter((leaf) => leaf in override.values);
      const differing = leaves.filter(
        (leaf) => !same(override.values[leaf], defaults[leaf])
      );

      if (differing.length === 0) {
        row.settings.push({
          key: `${group}:same`,
          label: text.groupLabel(group),
          value: text.sameAsDefault,
          orgDefault: '',
          same: true,
        });
        return;
      }
      differing.forEach((leaf) =>
        row.settings.push({
          key: `${group}:${leaf}`,
          label: text.labelFor(leaf),
          value: withUnit(leaf, override.values[leaf]),
          orgDefault:
            defaults[leaf] === undefined
              ? text.notSet
              : withUnit(leaf, defaults[leaf]),
        })
      );
    });
  });

  return [...rows.values()];
}
